import type { ReactNode } from 'react';
import { iconeDaPrata, iconeDoOuro } from '../../assets/items';
import { formatGold } from '../itemUi';
import { numero, t } from '../../i18n/idioma';
import { mostrarNovas, rotuloTecla, teclaDe, type Acao } from '../../input/atalhos';
import { StatBars, STATUS_VAZIO } from '../StatBars';
import type { WorldStatus } from '../../world/types';
import { FADIGA_CACADA, FADIGA_COLETA } from '../../world/fadiga';
import { IconeHud } from './icones';
import { MarcaUltimaIdle } from '../icones/IconesV3';
import { useTempoCacada } from './useTempoCacada';

export type Janela =
  | 'perfil' | 'mochila' | 'mercado' | 'loja' | 'cacar' | 'ordens' | 'guilda' | 'amigos'
  | 'config' | 'dano' | 'gm' | 'ferreiro' | 'alfaiate' | 'arqueiro'
  | 'npc' | 'corretor' | 'banco' | 'evento' | 'desempenho' | 'tell' | 'suporte' | 'rankings' | 'vender' | 'correio' | 'indicar';

export type PopId = 'viajar' | 'recursos' | 'lojas' | 'oficios' | 'treinar' | 'mais' | 'cidade' | 'social' | 'fadiga';

export interface ItemMenu {
  readonly id: string;
  readonly chave: string;
  readonly acao?: Acao;
  readonly pop?: PopId;
  readonly abre?: Janela;
  readonly trava?: boolean;
  readonly nova?: boolean;
}

/**
 * O topo em quatro grupos (techspec-hud-v3 §18): o farm (Caçar, Lojas, Leilão), a casa do personagem (Banco,
 * Ofícios, Guilda, Treinar; Amigos virou botão flutuante na §23 e deu o lugar ao Treinar na §24), o placar (Eventos, Rankings, Dano) e o Mais com o resto (Wiki, Configuração,
 * Suporte, GM). A Configuração fica no topo, antes do Mais. No celular o topo some e o Mais volta a listar Eventos, Rankings e Dano.
 */
const GRUPOS: ReadonlyArray<{ chave: string; itens: readonly ItemMenu[] }> = [
  {
    chave: 'hud.grupo.aventura',
    itens: [
      { id: 'cacar', chave: 'mundo.cacar', acao: 'cacar', abre: 'cacar' },
      { id: 'lojas', chave: 'hud.menu.lojas', acao: 'lojas', pop: 'lojas', trava: true, nova: true },
      { id: 'mercado', chave: 'hud.menu.leilao', acao: 'mercado', abre: 'mercado' }
    ]
  },
  {
    chave: 'hud.grupo.cidade',
    itens: [
      { id: 'banco', chave: 'banco.titulo', acao: 'banco', abre: 'banco', trava: true, nova: true },
      /* techspec-craft: a oficina saiu da tecla U para o topo */
      { id: 'oficios', chave: 'hud.menu.oficios', acao: 'oficios', pop: 'oficios', trava: true, nova: true },
      { id: 'guilda', chave: 'guilda.titulo', acao: 'guilda', abre: 'guilda' },
      /* §24: escolher a skill e ir ao manequim de treino mais perto (era o lugar de Amigos) */
      { id: 'treinar', chave: 'manequim.treinoTitulo', pop: 'treinar', trava: true }
    ]
  },
  {
    chave: 'hud.grupo.placar',
    itens: [
      { id: 'evento', chave: 'evento.mais.titulo', abre: 'evento' },
      { id: 'rankings', chave: 'rankings.janela', abre: 'rankings' },
      { id: 'dano', chave: 'dps.titulo', abre: 'dano' }
    ]
  },
  {
    chave: 'hud.grupo.mais',
    itens: [
      /* A Configuração saiu do Mais para o topo; o Mais fica por último. */
      { id: 'config', chave: 'hud.menu.config', acao: 'config', abre: 'config' },
      { id: 'mais', chave: 'hud.menu.mais', pop: 'mais' }
    ]
  }
];

export function itemMenu(id: string): ItemMenu | undefined {
  for (const g of GRUPOS) {
    const m = g.itens.find(i => i.id === id);
    if (m) return m;
  }
  return undefined;
}

/** Em caçada, a linha "● em caçada · mm:ss" embaixo de Caçar (no celular, só o tempo). */
export function SubCacada({ desde, curto = false }: { desde: string | null; curto?: boolean }) {
  const tempo = useTempoCacada(desde);
  return (
    <span className="v3-sub" hidden={!tempo}>
      <i />{tempo && (curto ? tempo : `${t('hud.cacar.emCacada')} · ${tempo}`)}
    </span>
  );
}

function BotaoMenu({
  m, pop, aberta, travado, pip, cacandoDesde, onMenu
}: {
  m: ItemMenu;
  pop: PopId | null;
  aberta: boolean;
  travado: boolean;
  pip: boolean;
  cacandoDesde: string | null;
  onMenu(m: ItemMenu): void;
}) {
  const rotulo = t(m.chave);
  const tecla = m.acao ? rotuloTecla(teclaDe(m.acao)) : '';
  return (
    <button
      type="button"
      className="mt"
      data-menu={m.id}
      {...(m.pop
        ? { 'aria-haspopup': 'true' as const, 'aria-expanded': pop === m.pop }
        : { 'aria-pressed': aberta })}
      aria-disabled={travado || undefined}
      title={tecla ? `${rotulo} (${tecla})` : rotulo}
      onClick={() => onMenu(m)}
    >
      {tecla && <span className={`tc${m.nova && mostrarNovas ? ' nova' : ''}`}>{tecla}</span>}
      {pip && <span className="kit-pip kit-pip--dot p-pip" aria-hidden="true" />}
      {m.id === 'cacar' ? <MarcaUltimaIdle /> : <IconeHud nome={m.id} />}
      <span className="mt-rot">{rotulo}{m.pop && <span className="seta">▾</span>}</span>
      {/* §21: no topo, só a luz verde e o tempo; o botão ficou menor */}
      {m.id === 'cacar' && <SubCacada desde={cacandoDesde} curto />}
      {travado && <span className="trava" aria-label={t('hud.soNaCidade')}>🔒</span>}
    </button>
  );
}

export function BarraTopo({
  quem, pop, janelaAberta, menuTravado, pips, onMenu,
  status, prata, ouro, lojaAberta, novidade, onLoja, cacandoDesde, onFadiga
}: {
  quem: ReactNode;
  pop: PopId | null;
  janelaAberta(id: string): boolean;
  menuTravado(m: ItemMenu): boolean;
  /** O ponto dourado por botão: Mais (suporte), Eventos e Rankings (prêmio a pegar). */
  pips: Readonly<Partial<Record<string, boolean>>>;
  onMenu(m: ItemMenu): void;
  status: WorldStatus | null;
  prata: number;
  ouro: number;
  lojaAberta: boolean;
  novidade: boolean;
  onLoja(): void;
  /** `startedAt` da ordem de caçada, ou null fora de caçada. */
  cacandoDesde: string | null;
  /** O clique no anel da stamina abre o resumo da fadiga (pop 'fadiga'). */
  onFadiga(): void;
}) {
  const gastando = status?.fadigaFaixa === FADIGA_CACADA || status?.fadigaFaixa === FADIGA_COLETA;
  return (
    <header className="topbar">
      <div className="topbar__left">{quem}</div>
      <nav className="mt-nav" aria-label={t('mundo.paineis')}>
        {GRUPOS.map(g => (
          <div className="mt-g" role="group" aria-label={t(g.chave)} key={g.chave}>
            {g.itens.map(m => (
              <BotaoMenu
                key={m.id}
                m={m}
                pop={pop}
                aberta={m.abre ? janelaAberta(m.abre) : false}
                travado={menuTravado(m)}
                pip={!!pips[m.id]}
                cacandoDesde={cacandoDesde}
                onMenu={onMenu}
              />
            ))}
          </div>
        ))}
      </nav>
      <div className="mt-dir">
        {/* HUD V3: a stamina à esquerda e a prata ao lado da Loja (o ouro mora no botão da Loja) */}
        <div className="topbar__moedas">
          {/* O anel abre o resumo da fadiga (docs/prototipos/fadiga-resumo-prototipo.html). */}
          <button
            type="button"
            className={`topbar__stamina${pop === 'fadiga' ? ' is-aberto' : ''}`}
            data-menu="fadiga"
            aria-haspopup="true"
            aria-expanded={pop === 'fadiga'}
            aria-label={t('fadiga.resumo.abrir')}
            onClick={onFadiga}
          >
            <StatBars status={status ?? STATUS_VAZIO} show={['fadiga']} anel />
            {gastando && <i className="fd-gasta" aria-hidden="true" />}
          </button>
          <span className="topbar__stat" title={`${t('mochila.prata')} · ${numero(prata)}`}>
            <img src={iconeDaPrata()} alt="" />
            <span>{formatGold(prata)}</span>
          </span>
        </div>
        <button
          type="button"
          className="mt-loja"
          aria-pressed={lojaAberta}
          aria-label={t('hud.loja.aria')}
          title={t('hud.loja.dica', { n: numero(ouro) })}
          onClick={onLoja}
        >
          <img src={iconeDoOuro()} alt="" />
          <i className="brilho" />
          <span className="rot">{t('hud.loja.rotulo')}<small>{t('hud.loja.ouroN', { n: numero(ouro) })}</small></span>
          {novidade ? <span className="novo">{t('hud.loja.novo')}</span> : <span className="v3-mais" aria-hidden="true">+</span>}
        </button>
      </div>
    </header>
  );
}
